<template>
  <div v-if="product" class="product-info">
    <h2 class="product-info heading light">Details</h2>
    <div class="content desktop flex-grid">
      <div class="product-info details">
        <div v-if="product.description" class="description-container row large">
          <h3 class="product-info detail-info-title">Description</h3>
          <p v-html="productDescriptionSanitized" />
        </div>
      </div>
      <div class="product-info details">
        <ProductFeature :product="product" class="row large" />
      </div>
      <div class="product-info details">
        <ProductSpecs :product="product" class="row large" />
      </div>
      <div class="product-info details">
        <ProductCare :product="product" class="row large" />
        <ProductAssembly :product="product" v-if="product.assemblyInstructionsUrl" class="row large" />
      </div>
      <div class="dimension-image-border" v-if="lastImage">
        <PreserveRatio :width="2890" :height="1500" class="row large" :key="lastImage.url">
          <div
            v-if="lastImage && mounted"
            ref="dimensionContainerDesktop"
            class="product-info dimension-container"
            :key="lastImage.url"
            @click="onExpandImage"
          >
            <div class="dimension-img">
              <ResponsiveImage :url="lastImage.url + '?fit=max&w={width}&q={q}'" :lazy="false" :alt="`Detailed dimensions of ${product.title}.`" />
            </div>
          </div>
          <ProductImageControls
            v-if="lastImage && lastImage.type === 'IMAGE'"
            :product="product"
            @zoom="onExpandImage"
            class="product-info-image-controls"
          ></ProductImageControls>
        </PreserveRatio>
      </div>
    </div>

    <div class="Product-info content mobile">
      <div v-for="(reason, index) in reasons" :key="index" class="mobile-content-wrapper" :ref="'mobileContentWrapper' + index">
        <Divider v-if="index <= reasons.length" height="1px" margin-top="var(--space-6)" margin-bottom="var(--space-6)" />
        <div class="mobile-content" @click.passive="onAccordionClick(index)">
          <h4 class="reason-title">
            <span class="reason-text">{{ reason }}</span>
            <span class="accordion-icons af" :class="[ItemSelected(index)]"></span>
          </h4>
        </div>
        <div class="accordion-content" :class="[isItemSelected(index) && 'active']">
          <div v-if="reason === 'Description'">
            <p v-html="productDescriptionSanitized" />
          </div>
          <div v-else-if="reason === 'Features'" style="margin-top: 10px">
            <ProductFeature :product="product" class="reason-description" />
          </div>
          <div v-else-if="reason === 'Specifications'">
            <div class="dimension-image-border" v-if="lastImage">
              <PreserveRatio :width="2890" :height="1500" class="row large" :key="lastImage.url">
                <div
                  v-if="lastImage && mounted"
                  ref="dimensionContainerDesktop"
                  class="product-info dimension-container"
                  :key="lastImage.url"
                  @click="onExpandImage"
                >
                  <div class="dimension-img">
                    <ResponsiveImage
                      :url="lastImage.url + '?fit=max&w={width}&q={q}'"
                      :lazy="false"
                      :alt="`Detailed dimensions of ${product.title}.`"
                    />
                  </div>
                </div>
                <ProductImageControls
                  v-if="lastImage && lastImage.type === 'IMAGE'"
                  :product="product"
                  @zoom="onExpandImage"
                  class="product-info-image-controls"
                ></ProductImageControls>
              </PreserveRatio>
            </div>
            <div
              v-if="product && isARButtonEnabled && mounted"
              :key="`product-viewer-button-${product.hashKey}-info-section`"
              class="product-viewer-button-mobile-info-section"
            >
              <ProductViewerButton :product="product" />
            </div>
            <ProductSpecs :product="product" class="reason-description" style="margin-top: 12px" />
          </div>
          <div v-else-if="reason === 'Care'">
            <ProductCare :product="product" class="reason-description" />
          </div>
          <div v-else-if="reason === 'Assembly Instructions'" class="Product-info assembly">
            <ProductAssembly :product="product" class="reason-description"  />
          </div>
        </div>
      </div>
      <Divider height="2px" margin-top="var(--space-6)" margin-bottom="var(--space-6)" />
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import PreserveRatio from '@/components/commons/PreserveRatio.vue';
import ProductAssembly from '@/components/product/ProductAssembly.vue';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';
import ProductFeature from '@/components/product/ProductFeature.vue';
import ProductSpecs from '@/components/product/ProductSpecs.vue';
import ProductCare from '@/components/product/ProductCare.vue';
import Tracking from '@/components/commons/Tracking';
import ZoomIcon from '@/assets/product/zoom-in.svg';
import useLifecycleHook from '@/compositions/useLifecycleHook';
import useExperiment from '@/compositions/useExperiment.js';
import Divider from '@/pixel/components/Divider.vue';
import ProductViewerButton from '@/components/product/ProductViewerButton.vue';
import ProductImageControls from '@/components/product/ProductImageControls.vue';
import sanitize from '@/utils/sanitizer.js';

export default {
  name: 'ProductNewInfo',
  components: {
    ProductFeature,
    ResponsiveImage,
    PreserveRatio,
    ProductSpecs,
    ProductCare,
    Divider,
    ProductAssembly,
    ZoomIcon,
    ProductImageControls,
    ProductViewerButton,
  },
  props: {
    showCollections: Boolean,
    product: {
      type: Object,
      default: null,
    },
    activeMedia: {
      default: 0,
      type: Number,
    },
    lastImage: {
      default: null,
      type: Object,
    },
  },
  emits: ['interaction', 'openGallery'],
  data() {
    return {
      selected: [0],
      count: 0,
    };
  },

  setup(props) {
    const { mounted } = useLifecycleHook();
    const { isFeatureVariant } = useExperiment();
    const isARButtonEnabled = computed(() => isFeatureVariant('ar-button', 'on'));
    const reasons = [];

    if (props.product && props.product.description.length !== 0) {
      reasons.push('Description');

      if (props.product && props.product.feature.length !== 0) {
        reasons.push('Features');
      }
    }
    if (props.product && props.product.spec.length !== 0) {
      reasons.push('Specifications');
    }
    if (props.product && props.product.care.length !== 0) {
      reasons.push('Care');
    }

    if (props.product && props.product.assemblyInstructionsUrl) {
      reasons.push('Assembly Instructions');
    }

    const productDescriptionSanitized = computed(() => {
      return sanitize(props.product.description);
    });

    return {
      reasons,
      mounted,
      isARButtonEnabled,
      productDescriptionSanitized,
    };
  },

  methods: {
    onAccordionClick(itemIndex) {
      const index = this.selected.indexOf(itemIndex);
      if (index > -1) {
        this.selected.splice(index, 1);
      } else {
        this.selected.push(itemIndex);
      }
      this.$emit('interaction', this.reasons[itemIndex]);
    },

    isItemSelected(itemIndex) {
      return this.selected.includes(itemIndex);
    },

    ItemSelected(itemIndex) {
      return this.isItemSelected(itemIndex) ? 'af-minus' : 'af-plus';
    },

    onExpandImage() {
      this.$emit('openGallery');
      Tracking.track('PDP Expand Product Image', {
        productId: this.product.hashKey,
        index: this.product.media && this.product.media.length ? this.product.media.length - 1 : null,
      });
    },
  },
};
</script>
<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.product-info {
  .heading {
    font-size: 2rem;
    margin-bottom: var(--space-6);
    color: var(--grey-dark);
    text-transform: none;
    letter-spacing: 0px;
  }

  .details {
    flex: 1 0 20%;
    margin-right: var(--space-10);
    justify-content: space-evenly;

    .title {
      margin-bottom: var(--space-4);
      text-transform: none;
      letter-spacing: 0px;
    }

    h3 {
      letter-spacing: 0px;
      text-transform: none;
    }

    @media (max-width: 1366px) {
      flex: 1 0 40%;
    }

    @media (min-width: 1600px) {
      margin-right: 27.92px;
    }
  }

  .dimension-img {
    cursor: zoom-in !important;
  }

  .dimension-image-border {
    width: inherit !important;
    border: 1px solid #f2f2f2;
    box-sizing: border-box;

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      margin-top: var(--space-10);
    }
  }

  .reason-description {
    line-height: var(--space-6);
    font-size: var(--font-size-16);
    overflow: hidden;
  }

  .content {
    &.desktop {
      @media (max-width: calc($tablet-breakpoint - 1px)) {
        display: none;
      }

      .row {
        &.large {
          margin-bottom: var(--space-5);
        }
      }
    }

    &.mobile {
      @media (min-width: $tablet-breakpoint) {
        display: none;
      }

      .mobile-content-wrapper {
        .mobile-content {
          display: flex;
          align-items: center;
          cursor: pointer;

          .reason-title {
            color: var(--grey-medium);
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;

            .reason-text {
              padding-right: var(--space-3);
              flex-basis: 90%;
              color: var(--grey-dark);
              text-transform: none;
              font-size: var(--space-5);
              letter-spacing: 0px !important;
            }

            .accordion-icons {
              background-color: transparent;
              width: var(--space-6);
              height: var(--space-6);
              text-align: center;
              border-radius: var(--space-3);
              flex-basis: 10%;
            }
          }
        }

        .accordion-content {
          transition: max-height ease-out 250ms;
          max-height: var(--space-0);
          overflow: hidden;

          &.active {
            max-height: var(--space-300);
            overflow: initial;
          }

          p {
            padding: var(--space-4) var(--space-0) var(--space-0) var(--space-0);
            margin: var(--space-0);
            line-height: var(--space-6);
            font-size: var(--font-size-16);
          }
        }

        .assembly {
          margin-top: var(--space-4);
          margin-bottom: 10px;
        }
      }

      .dimension-container {
        width: inherit;
        overflow: hidden;
      }

      .divider {
        @media (max-width: calc($tablet-breakpoint - 1px)) {
          display: none;
        }
      }
    }

    .zoom-btn {
      display: block;
      height: var(--space-10);
      width: var(--space-10);
      border-radius: 50%;
      border: 1px solid;
      background-color: var(--white);
      border-color: var(--grey-light);
      right: var(--space-4);
      padding: 11px !important;
    }
  }
}

.product-viewer-button-mobile-info-section {
  display: block;
  width: 100%;
  margin-top: var(--space-6);
  margin-bottom: var(--space-10);

  @media (min-width: $mobile-breakpoint) {
    display: none;
  }
}

/* purgecss ignore */
::v-deep(.detail-info-title) {
  margin-bottom: 16px;
  text-transform: none;
  color: #626262;
  letter-spacing: 0px;

  @media (max-width: calc($tablet-breakpoint - 1px)) {
    display: none;
  }
}
</style>
